import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import anime from 'animejs/lib/anime.es.js';

import { AppStateService } from 'app/services/app-state.service';
import { FlightOptionService } from 'app/services/flight-option-service';
import { FlightWatcherService } from 'app/services/flight-watcher-service';

import { PageWrapperComponent } from 'app/components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { FlightOptionCardComponent } from 'app/components/distinct-components/cards/flight-option-card/flight-option-card.component';
import { RequestConnectionOverlayComponent } from 'app/components/distinct-components/overlays/request-connection-overlay/request-connection-overlay.component';
import { SectionLabelComponent } from 'app/components/ui-kit/typography/section-label/section-label.component';
import { ToggleItemRowComponent } from 'app/components/ui-kit/toggles/toggle-item-row/toggle-item-row.component';
import { Subscription } from 'rxjs';
import { ProviderNames, normalizeProvider } from 'app/constants/provider-constants';
import { PersistentWatcherService } from 'app/services/persistent-watcher.service';
import { holdPls } from 'app/utils/time/timing-utils';


@Component({
  selector: 'app-view-watcher',
  standalone: true,
  imports: [CommonModule, FlightOptionCardComponent, PageWrapperComponent, RequestConnectionOverlayComponent, SectionLabelComponent, ToggleItemRowComponent],
  templateUrl: './view-watcher.component.html',
  styleUrl: './view-watcher.component.scss'
})
export class ViewWatcherComponent {
  @Input({ transform: (v: any) => v === '' ? true : !!v }) isPersistent = false;

  watcherFlights: any;
  userFlightOption: any;
  isLoading: boolean = true;
  private routeSub: Subscription;
  providerOptions: { value: string; label: string }[] = [];
  selectedProvider: string = ProviderNames.ALL_INDEPENDENT;
  filteredWatcherFlights: any[] = [];
  private preferredProviders: string[] = [];

  constructor(
    private route: ActivatedRoute, private flightWatcherService: FlightWatcherService,
    private flightOptionService: FlightOptionService, public appState: AppStateService,
    private persistentWatcherService: PersistentWatcherService
  ) { }

  ngOnInit() {
    this.routeSub = this.route.paramMap.subscribe(params => {
      const watcherId = params.get('id');
      if (watcherId) {
        this.loadWatcherData(watcherId);
      }
      const backButton = {
        action: 'route',
        value: '/watchers'
      };
      this.appState.navigation.backButtonState.set(backButton);
    });
  }

  async loadWatcherData(watcherId) {
    const profile = this.appState.user.profile();
    if (profile && profile.preferredProviders && profile.preferredProviders.length > 0) {
      this.preferredProviders = profile.preferredProviders.map(normalizeProvider);
    }
    try {

      let data = null;
      if (this.isPersistent) {
        data = await this.persistentWatcherService.getFlightWatcher(watcherId);
      } else {
        data = await this.flightWatcherService.getFlightWatcher(watcherId);
      }

      let userFlightOption = null;

      if (!this.isPersistent) {
        userFlightOption = await this.flightOptionService.getFlightOption(data.flightOptionId);
        console.log('user flight option first', userFlightOption);
        this.userFlightOption = userFlightOption;
      }

      const flightPromises = (data?.discoveredFlights || []).map(async (flight) => {
        const flightData = await this.flightOptionService.getFlightOption(flight.id);
        if (flightData) {
          return {
            ...flightData,
            discoveredAt: flight.discoveredAt,
          };
        }
        return null;
      });

      const resolvedFlights = await Promise.all(flightPromises);
      this.watcherFlights = resolvedFlights.filter(Boolean);
      this.watcherFlights.sort((a, b) => a.discoveredAt.toMillis() - b.discoveredAt.toMillis());
      this.watcherFlights = this.sortOptionsByPreferredProviders(this.watcherFlights);
      this.configureProviderOptions();
      this.applyProviderFilter();

      console.log('WATCHER FLIGHTS', this.watcherFlights);

    } catch (err) {
      console.error(err);
    } finally {
      this.isLoading = false;
      setTimeout(() => {
        this.animateResults();
      }, 100);
    }
  }

  onClickOption(opt) {
    const selectedOption = opt;
    this.appState.agent.selectedOption.set(selectedOption);
  }

  filterOptions(options: any[]) {
    if (!options) return [];
    const filteredOptions = this.selectedProvider === ProviderNames.ALL_INDEPENDENT
      ? [...options]
      : options.filter(o => {
        const providers = this.getOptionProviders(o);
        return providers.includes(this.selectedProvider);
      });

    return this.sortOptionsByPreferredProviders(filteredOptions);
  }

  async onFilterValueChange() {
    this.applyProviderFilter();
    await holdPls(500);
    this.animateResults();
  }

  animateResults() {
    anime({
      targets: '#r-card',
      duration: 650,
      opacity: [0, 1],
      translateY: [12, 0],
      delay: anime.stagger(75),
      direction: 'alternate',
      easing: 'spring(1, 80, 10, 2)',
      loop: false,
    });
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  clearProviderFilter() {
    this.selectedProvider = ProviderNames.ALL_INDEPENDENT;
    this.applyProviderFilter();
    this.animateResults();
  }

  get selectedProviderLabel(): string {
    return this.providerOptions.find(option => option.value === this.selectedProvider)?.label || 'All';
  }

  get shouldShowProviderFilter(): boolean {
    return this.providerOptions.filter(option => option.value !== ProviderNames.ALL_INDEPENDENT).length > 1;
  }

  private applyProviderFilter() {
    this.filteredWatcherFlights = this.filterOptions(this.watcherFlights || []);
  }

  private configureProviderOptions() {
    const providers = new Set<string>();
    (this.watcherFlights || []).forEach(option => this.getOptionProviders(option).forEach(provider => providers.add(provider)));
    const options = [
      { value: ProviderNames.ALL_INDEPENDENT, label: 'All' },
      { value: ProviderNames.NETJETS, label: ProviderNames.NETJETS },
      { value: ProviderNames.WHEELS_UP, label: 'Wheels Up' },
      { value: ProviderNames.FLEXJET, label: 'Flexjet' },
    ];
    this.providerOptions = [
      options[0],
      ...options.slice(1).filter(option => providers.has(option.value))
    ];
    if (!this.providerOptions.some(option => option.value === this.selectedProvider)) {
      this.selectedProvider = ProviderNames.ALL_INDEPENDENT;
    }
  }

  private getOptionProviders(option: any): string[] {
    const membershipProviders = Array.isArray(option?.memberships)
      ? option.memberships.map(m => normalizeProvider(m.organization ?? m.provider ?? 'Independent'))
      : [];
    const provider = normalizeProvider(option?.membershipOrganization || option?.provider || '');
    return Array.from(new Set([...membershipProviders, provider].filter(Boolean)));
  }

  private getProviderPriority(option: any): number {
    if (this.preferredProviders.length === 0) {
      return Number.POSITIVE_INFINITY;
    }

    const providers = this.getOptionProviders(option);
    const priority = providers
      .map(p => this.preferredProviders.indexOf(p))
      .filter(index => index >= 0)
      .sort((a, b) => a - b)[0];

    return priority !== undefined ? priority : Number.POSITIVE_INFINITY;
  }

  private sortOptionsByPreferredProviders(options: any[] = []): any[] {
    if (!options?.length || this.preferredProviders.length === 0) {
      return options ?? [];
    }

    return [...options].sort((a, b) => this.getProviderPriority(a) - this.getProviderPriority(b));
  }

}
